/**
 * Comment Drawer Component
 * 
 * Drawer for Process Owner to add comments to audit team
 * Features:
 * - Text area for comment input
 * - Sends comment on submit
 */

import React, { useState, useEffect } from 'react';
import { Form, Input, Button } from 'antd';
import styled from 'styled-components';
import RightContentDrawer from '../../../../../components/common/RightContentDrawer';
import ButtonGroup from '../../../../../components/common/ButtonGroup';
import type { CertificationCommentType } from '../../../../../services/certificationService';

const { TextArea } = Input;

// ========== TYPE DEFINITIONS ==========

export interface CommentFormData {
  comment: string;
  commentType: CertificationCommentType | null;
}

export interface CommentDrawerProps {
  open: boolean;
  commentType: CertificationCommentType | null;
  onClose: () => void;
  onSubmit: (data: CommentFormData) => void;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledTextArea = styled(TextArea)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #1F2937;
  min-height: 150px !important;

  &.ant-input {
    color: #1F2937;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

// ========== MAIN COMPONENT ==========

const CommentDrawer: React.FC<CommentDrawerProps> = ({
  open,
  commentType,
  onClose,
  onSubmit,
}) => {
  // Form states
  const [form] = Form.useForm();
  const [comment, setComment] = useState('');

  // UI states
  const [isFormValid, setIsFormValid] = useState(false);

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  // Watch for field changes and validate
  useEffect(() => {
    const hasComment = !!comment && comment.trim() !== '';
    setIsFormValid(hasComment);
  }, [comment]);

  const resetForm = () => {
    setComment('');
    form.resetFields();
  };

  const handleClose = () => {
    setIsFormValid(false);
    resetForm();
    onClose();
  };

  const handleFormSubmit = () => {
    onSubmit({
      comment: comment.trim(),
      commentType,
    });
    handleClose();
  };

  const getCommentTypeLabel = () => {
    switch (commentType) {
      case 'riskAssessment':
        return 'Risk Assessment';
      case 'businessKnowledge':
        return 'Business Knowledge';
      case 'flowcharts':
        return 'Flowcharts';
      case 'walkthroughs':
        return 'Walkthroughs';
      case 'testSheet':
        return 'Test Sheet';
      case 'gaps':
        return 'Gaps';
      default:
        return '';
    }
  };

  return (
    <RightContentDrawer
      open={open}
      title="Add Comment"
      width={572}
      onClose={handleClose}
      footer={
        <ButtonGroup>
          <Button
            type="default"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleFormSubmit}
            disabled={!isFormValid}
          >
            Send Comment
          </Button>
        </ButtonGroup>
      }
    >
      <StyledForm
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        {/* Comment */}
        <Form.Item
          name="comment"
          rules={[
            { required: true, message: 'Please enter your comment' },
            { whitespace: true, message: 'Comment cannot be empty' },
          ]}
        >
          <div>
            <FormLabel>Comment {commentType && `for ${getCommentTypeLabel()}`}</FormLabel>
            <StyledTextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Enter your comment for the audit team..."
              rows={6}
              spellCheck={true}
              autoFocus
            />
            <InfoText>
              Your comment will be sent to the audit team for review.
            </InfoText>
          </div>
        </Form.Item>
      </StyledForm>
    </RightContentDrawer>
  );
};

export default CommentDrawer;

